import type {
  ChartContrast,
  ChartSyllable,
  ChartTone,
} from "../content/pinyin-chart/pinyin-chart.js";
import { drawFormula, drawPanel } from "./pinyin-chart-panel.js";
import { ChartPlayer, type PlayStep } from "./pinyin-chart-player.js";
import { startPlayground } from "./pinyin-chart-playground-field.js";
import { ToneQuiz, quizPool } from "./pinyin-chart-quiz.js";
import {
  type ChartVoice,
  chartSilenceAddress,
  voiceFile,
} from "../content/pinyin-chart/pinyin-chart-voices.js";
import { searchChart } from "./pinyin-chart-search.js";

/** What the page embeds for the script. */
interface EmbeddedChart {
  readonly syllables: readonly ChartSyllable[];
  readonly contrasts: readonly ChartContrast[];
}

/** What a reader chose, kept in this browser. */
interface ChartSettings {
  voice: ChartVoice;

  /** "all", or the one tone a press plays. */
  tap: string;
  rare: boolean;
}

/**
 * The syllable a tone choice plays as it is chosen. The classic one, and a
 * better recording than the chart's a, which is the obvious alternative.
 */
const sampleSyllable = "ma";

/** Where the settings are kept. Per browser, and nothing breaks without it. */
const storageKey = "cb-pinyin-chart";

const defaults: ChartSettings = { voice: "female", tap: "all", rare: false };

function loadSettings(storage: Storage | undefined): ChartSettings {
  try {
    const stored = storage?.getItem(storageKey);
    const parsed: unknown =
      stored === null || stored === undefined ? {} : JSON.parse(stored);

    if (typeof parsed !== "object" || parsed === null) {
      return { ...defaults };
    }

    const found = parsed as Partial<ChartSettings>;

    return {
      voice: found.voice === "male" ? "male" : "female",
      tap: ["all", "1", "2", "3", "4"].includes(String(found.tap))
        ? String(found.tap)
        : "all",
      rare: found.rare === true,
    };
  } catch {
    return { ...defaults };
  }
}

function saveSettings(
  storage: Storage | undefined,
  settings: ChartSettings,
): void {
  try {
    storage?.setItem(storageKey, JSON.stringify(settings));
  } catch {
    // A browser refusing storage keeps the choice for this visit only.
  }
}

/** The page's storage, or undefined where touching it throws. */
function pageStorage(window: Window | null): Storage | undefined {
  try {
    return window?.localStorage ?? undefined;
  } catch {
    return undefined;
  }
}

/**
 * Start the pinyin chart.
 *
 * Everything the reader sees is in the markup, from
 * `partials/tools/pinyin-chart.html`. This makes it play. A press on a
 * syllable selects it, draws the panel under the grid and plays the tones the
 * reader asked for, in the voice they chose.
 * @param container - The `[data-cb-tool="pinyin-chart"]` element.
 * @param stand - What a test stands in for: the audio, the gap between files
 * and the quiz's random numbers.
 */
export function startPinyinChart(
  container: HTMLElement,
  stand: {
    readonly audio?: ConstructorParameters<typeof ChartPlayer>[0];
    readonly wait?: ConstructorParameters<typeof ChartPlayer>[1];
    readonly random?: () => number;
  } = {},
): void {
  const document = container.ownerDocument;
  const window = document.defaultView;
  const source = container.querySelector('[data-cb-pc="data"]');

  if (source === null || source.textContent === "") {
    return;
  }

  const data = JSON.parse(source.textContent) as EmbeddedChart;
  const byKey = new Map(
    data.syllables.map((syllable) => [syllable.key, syllable]),
  );
  const storage = pageStorage(window);
  const settings = loadSettings(storage);
  const player = new ChartPlayer(stand.audio ?? new Audio(), stand.wait);
  const panel = container.querySelector<HTMLElement>('[data-cb-pc="panel"]');
  const formula = container.querySelector<HTMLElement>(
    '[data-cb-pc="formula"]',
  );
  const grid = container.querySelector<HTMLElement>('[data-cb-pc="grid"]');
  const status = container.querySelector<HTMLElement>(
    '[data-cb-pc="search-result"]',
  );
  let selected: ChartSyllable | undefined;

  const cells = (): readonly HTMLElement[] => [
    ...container.querySelectorAll<HTMLElement>("[data-syllable]"),
  ];
  const cellOf = (key: string): HTMLElement | undefined =>
    cells().find((cell) => cell.dataset["syllable"] === key);
  const srcOf = (recording: string): string =>
    voiceFile(recording, settings.voice);

  /*
   * Light up where a cell sits: its row, its column, the final and initial
   * heading them and the group of initials its column belongs to. Once for
   * the syllable chosen and once for the one under the pointer, each with a
   * class of its own so that moving the pointer leaves the choice marked.
   */
  const cross = (
    cell: HTMLElement | null,
    kind: "selected" | "hover",
  ): void => {
    const mark = `is-${kind}-cross`;

    for (const marked of grid?.querySelectorAll(`.${mark}`) ?? []) {
      marked.classList.remove(mark);
    }

    const td = cell?.closest("td");
    const tr = td?.closest("tr");

    if (
      grid === null ||
      td === null ||
      td === undefined ||
      tr === null ||
      tr === undefined
    ) {
      return;
    }

    const column = [...tr.children].indexOf(td);
    let reached = 1;

    for (const part of tr.children) {
      part.classList.add(mark);
    }

    for (const row of grid.querySelectorAll("tr[data-row], .cb-pc-initials")) {
      row.children[column]?.classList.add(mark);
    }

    for (const group of grid.querySelectorAll<HTMLTableCellElement>(
      ".cb-pc-groups th",
    )) {
      if (column >= reached && column < reached + group.colSpan) {
        group.classList.add(mark);
      }

      reached += group.colSpan;
    }
  };

  grid?.addEventListener("pointerover", (event) => {
    const cell = (event.target as Element | null)?.closest<HTMLElement>(
      "[data-syllable]",
    );

    cross(cell ?? null, "hover");
  });

  grid?.addEventListener("pointerleave", () => {
    cross(null, "hover");
  });

  /*
   * Show which tone is sounding, in four places at once. The chosen cell
   * grows over its neighbours and writes the syllable with that tone's mark
   * and ink, its dot for the tone pulses, the tone's card in the panel is
   * ringed, and the tone's choice above the grid lights up. Called with
   * nothing when a run ends, which puts every one of them back.
   */
  const markPlaying = (tone: number | "neutral" | undefined): void => {
    for (const sounding of container.querySelectorAll<HTMLElement>(
      ".cb-pc-cell.is-sounding",
    )) {
      const written = sounding.querySelector<HTMLElement>(".cb-pc-py");

      sounding.classList.remove("is-sounding");

      if (written !== null) {
        // A cell is marked sounding only once its plain pinyin is kept below.
        written.textContent = written.dataset["plain"]!;
        written.className = "cb-pc-py";
      }
    }

    const cell = cellOf(selected?.key ?? "");
    const toned = selected?.tones.find((found) => found.tone === tone);
    const written = cell?.querySelector<HTMLElement>(".cb-pc-py");

    if (
      cell !== undefined &&
      toned !== undefined &&
      written !== null &&
      written !== undefined
    ) {
      written.dataset["plain"] ??= written.textContent;
      written.textContent = toned.pinyin;
      written.className = `cb-pc-py py-tone-${String(toned.tone)}`;
      cell.classList.add("is-sounding");
    }

    for (const [at, dot] of [
      ...(cell?.querySelectorAll(".cb-pc-dot") ?? []),
    ].entries()) {
      dot.classList.toggle("is-playing", tone === at + 1);
    }

    for (const choice of container.querySelectorAll<HTMLElement>(
      ".cb-pc-tap-tone",
    )) {
      choice.classList.toggle(
        "is-sounding",
        choice.dataset["tone"] === String(tone),
      );
    }

    for (const card of panel?.querySelectorAll<HTMLElement>(".cb-pc-tone") ??
      []) {
      const playing =
        tone === "neutral"
          ? card.classList.contains("is-neutral")
          : card.dataset["tone"] === String(tone);

      card.classList.toggle("is-playing", playing);
    }
  };

  const playTones = async (tones: readonly ChartTone[]): Promise<void> => {
    const steps: PlayStep[] = tones.flatMap((tone) =>
      tone.audio === undefined
        ? []
        : [
            {
              src: srcOf(tone.audio),
              onStart: () => {
                markPlaying(tone.tone);
              },
            },
          ],
    );

    // A tone the syllable is not read in, or has no recording of, answers
    // with the chart's silence rather than with nothing.
    const outcome = await player.play(
      steps.length === 0 ? [{ src: chartSilenceAddress }] : steps,
    );

    if (outcome !== "stopped") {
      markPlaying(undefined);
    }

    if (outcome === "failed" && status !== null) {
      status.textContent = "The recording could not be played.";
    }
  };

  const tapTones = (syllable: ChartSyllable): readonly ChartTone[] =>
    settings.tap === "all"
      ? syllable.tones
      : syllable.tones.filter((tone) => String(tone.tone) === settings.tap);

  const scroll = container.querySelector<HTMLElement>('[data-cb-pc="scroll"]');

  /*
   * Bring a cell into view inside the grid's own scrolling box, clear of the
   * sticky headers. `scrollIntoView` would scroll the page as well, and would
   * leave the cell under the header it is meant to be read against.
   */
  const reveal = (cell: HTMLElement | undefined): void => {
    if (scroll === null || cell === undefined) {
      return;
    }

    const box = scroll.getBoundingClientRect();
    const at = cell.getBoundingClientRect();
    const top =
      scroll.querySelector("thead")?.getBoundingClientRect().height ?? 0;
    const left =
      scroll.querySelector(".cb-pc-final")?.getBoundingClientRect().width ?? 0;
    const margin = 8;

    if (at.top < box.top + top) {
      scroll.scrollTop -= box.top + top - at.top + margin;
    } else if (at.bottom > box.bottom) {
      scroll.scrollTop += at.bottom - box.bottom + margin;
    }

    if (at.left < box.left + left) {
      scroll.scrollLeft -= box.left + left - at.left + margin;
    } else if (at.right > box.right) {
      scroll.scrollLeft += at.right - box.right + margin;
    }
  };

  /*
   * Bring a cell found by a search or a link to the middle of the screen. The
   * grid's box can run below the fold, so the page has to move as well as the
   * box, and `scrollIntoView` moves every scrolling ancestor. Centred in the
   * box, a cell is clear of the sticky headers wherever the grid can scroll.
   */
  const bringIntoView = (cell: HTMLElement | undefined): void => {
    if (cell === undefined || typeof cell.scrollIntoView !== "function") {
      return;
    }

    const still =
      window?.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? true;

    cell.scrollIntoView({
      block: "center",
      inline: "center",
      behavior: still ? "auto" : "smooth",
    });
  };

  const focusCell = (cell: HTMLElement): void => {
    for (const other of cells()) {
      other.tabIndex = other === cell ? 0 : -1;
    }

    cell.focus({ preventScroll: true });
    reveal(cell);
  };

  const select = (
    syllable: ChartSyllable,
    options: { readonly play: boolean; readonly tone?: number },
  ): void => {
    selected = syllable;

    for (const cell of cells()) {
      const chosen = cell.dataset["syllable"] === syllable.key;

      cell.classList.toggle("is-selected", chosen);
      cell.toggleAttribute("aria-current", chosen);
      cell.tabIndex = chosen ? 0 : -1;
    }

    const cell = cellOf(syllable.key);

    if (syllable.rare) {
      cell?.classList.add("is-revealed");
    }

    cross(cell ?? null, "selected");

    if (panel !== null) {
      drawPanel(panel, syllable);
    }

    if (formula !== null) {
      drawFormula(formula, syllable);
    }

    window?.history.replaceState(
      null,
      "",
      `#${options.tone === undefined ? syllable.key : `${syllable.key}${String(options.tone)}`}`,
    );

    if (options.play) {
      const tones =
        options.tone === undefined
          ? tapTones(syllable)
          : syllable.tones.filter((tone) => tone.tone === options.tone);

      void playTones(tones);
    }
  };

  // Settings.
  const applySettings = (): void => {
    container.classList.toggle("shows-rare", settings.rare);

    for (const input of container.querySelectorAll<HTMLInputElement>(
      "[data-cb-pc]",
    )) {
      const field = input.dataset["cbPc"];

      if (field === "voice" || field === "tap") {
        input.checked = input.value === settings[field];
      } else if (field === "rare") {
        input.checked = settings.rare;
      }
    }
  };

  const romanisation = document.documentElement.getAttribute(
    "data-cb-romanisation",
  );

  container.classList.toggle("is-bopomofo", romanisation === "bopomofo");

  for (const input of container.querySelectorAll<HTMLInputElement>(
    'input[data-cb-pc="script"]',
  )) {
    input.checked =
      input.value === (romanisation === "bopomofo" ? "bopomofo" : "pinyin");
  }

  container.addEventListener("change", (event) => {
    const input = event.target;

    if (!(input instanceof HTMLInputElement)) {
      return;
    }

    switch (input.dataset["cbPc"]) {
      case "voice":
        settings.voice = input.value === "male" ? "male" : "female";
        break;
      case "tap":
        settings.tap = input.value;
        break;
      case "rare":
        settings.rare = input.checked;
        break;
      case "script":
        container.classList.toggle("is-bopomofo", input.value === "bopomofo");

        return;
      case undefined:
      default:
        return;
    }

    saveSettings(storage, settings);
    applySettings();

    // A tone choice says what it does by doing it, on the syllable chosen,
    // or on ma before one is.
    const sample = selected ?? byKey.get(sampleSyllable);

    if (input.dataset["cbPc"] === "tap" && sample !== undefined) {
      void playTones(tapTones(sample));
    }
  });

  applySettings();

  // Pressing a syllable.
  container.addEventListener("click", (event) => {
    const target = event.target;

    if (!(target instanceof Element)) {
      return;
    }

    const cell = target.closest<HTMLElement>("[data-syllable]");

    if (cell !== null) {
      if (
        event instanceof MouseEvent &&
        (event.metaKey || event.ctrlKey || event.shiftKey)
      ) {
        return;
      }

      // The cell was found by its `data-syllable`.
      const syllable = byKey.get(cell.dataset["syllable"]!);

      if (syllable !== undefined) {
        event.preventDefault();
        select(syllable, { play: true });
      }

      return;
    }

    const control = target.closest<HTMLElement>(
      "[data-cb-pc-play-tone], [data-cb-pc-play-all], [data-cb-pc-play-neutral]",
    );

    if (control !== null && selected !== undefined) {
      if (control.dataset["cbPcPlayTone"] !== undefined) {
        const wanted = Number(control.dataset["cbPcPlayTone"]);

        void playTones(selected.tones.filter((tone) => tone.tone === wanted));
      } else if (control.dataset["cbPcPlayAll"] !== undefined) {
        void playTones(selected.tones);
      } else if (selected.neutral !== undefined) {
        markPlaying("neutral");
        void player
          .play([{ src: srcOf(selected.neutral.audio) }])
          .then((outcome) => {
            if (outcome !== "stopped") {
              markPlaying(undefined);
            }
          });
      }
    }
  });

  // Moving around the grid with the keyboard.
  grid?.addEventListener("keydown", (event) => {
    const cell = (event.target as Element | null)?.closest<HTMLElement>(
      "[data-syllable]",
    );

    if (cell === null || cell === undefined) {
      return;
    }

    // The cell was found by its `data-syllable`.
    const syllable = byKey.get(cell.dataset["syllable"]!);

    if (/^[1-4]$/u.test(event.key) && syllable !== undefined) {
      event.preventDefault();
      select(syllable, { play: true, tone: Number(event.key) });

      return;
    }

    if (event.key === " " && syllable !== undefined) {
      event.preventDefault();
      select(syllable, { play: true });

      return;
    }

    const moves: Readonly<Record<string, readonly [number, number]>> = {
      ArrowUp: [-1, 0],
      ArrowDown: [1, 0],
      ArrowLeft: [0, -1],
      ArrowRight: [0, 1],
    };
    const move = moves[event.key];
    const td = cell.closest("td");
    const tr = td?.closest("tr");

    if (
      move === undefined ||
      td === null ||
      td === undefined ||
      tr === null ||
      tr === undefined
    ) {
      return;
    }

    event.preventDefault();

    const rows = [...grid.querySelectorAll("tr[data-row]")];
    let row = rows.indexOf(tr);
    let column = [...tr.children].indexOf(td);

    for (let step = 0; step < 60; step++) {
      row += move[0];
      column += move[1];

      const found =
        rows[row]?.children[column]?.querySelector<HTMLElement>(
          "[data-syllable]",
        );

      if (rows[row] === undefined || column < 1) {
        return;
      }

      if (
        found !== null &&
        found !== undefined &&
        (!found.classList.contains("is-rare") ||
          settings.rare ||
          found.classList.contains("is-revealed"))
      ) {
        focusCell(found);

        return;
      }
    }
  });

  // Search.
  const search = container.querySelector<HTMLInputElement>(
    '[data-cb-pc="search"]',
  );

  search?.addEventListener("input", () => {
    if (status === null) {
      return;
    }

    const text = search.value;

    status.replaceChildren();

    if (text.trim() === "") {
      return;
    }

    const matches = searchChart(text, data.syllables);

    if (matches.length === 0) {
      status.textContent = "No matching syllable or character found.";

      return;
    }

    // A search with no match has returned above.
    const first = matches[0]!;

    select(first.syllable, {
      play: false,
      ...(first.tone !== undefined && { tone: first.tone.tone }),
    });

    bringIntoView(cellOf(first.syllable.key));

    if (matches.length > 1) {
      status.append("Choose ");

      matches.forEach((match, at) => {
        if (at > 0) {
          status.append(at === matches.length - 1 ? " or " : ", ");
        }

        const button = document.createElement("button");

        button.type = "button";
        button.className = "cb-pc-search-choice";
        button.textContent = match.tone?.pinyin ?? match.syllable.pinyin;
        button.addEventListener("click", () => {
          select(match.syllable, {
            play: true,
            ...(match.tone !== undefined && { tone: match.tone.tone }),
          });
        });
        status.append(button);
      });
    }
  });

  // The sounds that are easy to mix up.
  for (const button of container.querySelectorAll<HTMLButtonElement>(
    "[data-cb-pc-sound]",
  )) {
    button.addEventListener("click", () => {
      // The button was found by its `data-cb-pc-sound`.
      const key = button.dataset["cbPcSound"]!;
      const tone = data.syllables
        .flatMap((syllable) => syllable.tones)
        .find((found) => found.key === key);

      if (tone?.audio !== undefined) {
        void player.play([{ src: srcOf(tone.audio) }]);
      }
    });
  }

  for (const button of container.querySelectorAll<HTMLButtonElement>(
    "[data-cb-pc-contrast]",
  )) {
    button.addEventListener("click", () => {
      const contrast = data.contrasts[Number(button.dataset["cbPcContrast"])];
      const group = button.closest(".cb-pc-contrast");

      if (contrast === undefined) {
        return;
      }

      const tones = contrast.sounds.flatMap((sound) =>
        data.syllables
          .flatMap((syllable) => syllable.tones)
          .filter((tone) => tone.key === sound.key),
      );
      const steps = [...tones, ...tones].flatMap((tone) =>
        tone.audio === undefined
          ? []
          : [
              {
                src: srcOf(tone.audio),
                onStart: (): void => {
                  for (const sound of group?.querySelectorAll<HTMLElement>(
                    "[data-cb-pc-sound]",
                  ) ?? []) {
                    sound.classList.toggle(
                      "is-playing",
                      sound.dataset["cbPcSound"] === tone.key,
                    );
                  }
                },
              },
            ],
      );

      void player.play(steps).then(() => {
        for (const sound of group?.querySelectorAll(".is-playing") ?? []) {
          sound.classList.remove("is-playing");
        }
      });
    });
  }

  // The words that show a tone changing.
  for (const button of document.querySelectorAll<HTMLButtonElement>(
    "[data-cb-pc-word]",
  )) {
    button.addEventListener("click", () => {
      const src =
        settings.voice === "male"
          ? button.dataset["male"]
          : button.dataset["female"];

      if (src !== undefined) {
        void player.play([{ src }]);
      }
    });
  }

  const tonesByKey = new Map(
    data.syllables.flatMap((syllable) =>
      syllable.tones.map((tone) => [tone.key, tone] as const),
    ),
  );

  startPlayground(container, {
    player,
    toneOf: (key) => tonesByKey.get(key),
    srcOf,
  });
  startQuiz(container, data.syllables, player, stand.random);

  for (const hidden of container.querySelectorAll<HTMLElement>(
    "[data-cb-pc-needs-script]",
  )) {
    hidden.hidden = false;
  }

  /*
   * A link straight to a syllable, `#ma` or `#ma3`, selects it without a
   * sound. On arrival, and again when a link inside the page changes the hash.
   * `select` writes the hash with `replaceState`, which fires no event.
   */
  const followFragment = (): boolean => {
    const fragment = /^#([a-zê]+?)([1-4])?$/u.exec(
      decodeURIComponent(window?.location.hash ?? ""),
    );
    // The first group of the pattern is not optional.
    const linked = fragment === null ? undefined : byKey.get(fragment[1]!);

    if (linked === undefined || fragment === null) {
      return false;
    }

    select(linked, {
      play: false,
      ...(fragment[2] !== undefined && { tone: Number(fragment[2]) }),
    });
    bringIntoView(cellOf(linked.key));

    return true;
  };

  window?.addEventListener("hashchange", () => {
    followFragment();
  });

  if (!followFragment()) {
    const first = cells()[0];

    if (first !== undefined) {
      first.tabIndex = 0;
    }
  }
}

/** Show or hide part of the page, which may be missing. */
function show(element: HTMLElement | null, shown: boolean): void {
  if (element !== null) {
    element.hidden = !shown;
  }
}

/** The ordinal a quiz message names a tone with. */
const toneOrdinals = ["", "first", "second", "third", "fourth"];

/** The steps the quiz moves through, which `data-state` on it records. */
type QuizState = "idle" | "asking" | "answered" | "done";

/**
 * The listening quiz under the chart.
 *
 * It moves through four steps. Idle shows the explanation and a button to
 * start. Asking plays a syllable and takes one answer. Answered marks the
 * answer, locks the four buttons and offers the next syllable. Done gives the
 * round's score and offers another round. Nothing can be pressed out of turn,
 * because each step shows and enables only its own controls.
 * @param container - The chart, which holds `[data-cb-pc="quiz"]`.
 * @param syllables - What questions are drawn from.
 * @param player - The chart's player, so a question stops the chart sounding.
 * @param random - The quiz's random numbers, which a test fixes.
 */
function startQuiz(
  container: HTMLElement,
  syllables: readonly ChartSyllable[],
  player: ChartPlayer,
  random?: () => number,
): void {
  const section = container.querySelector<HTMLElement>('[data-cb-pc="quiz"]');

  if (section === null) {
    return;
  }

  const quiz = new ToneQuiz(quizPool(syllables), random);
  const button = (attribute: string): HTMLButtonElement | null =>
    section.querySelector<HTMLButtonElement>(`button[${attribute}]`);
  const part = (attribute: string): HTMLElement | null =>
    section.querySelector<HTMLElement>(`[${attribute}]`);
  const start = button("data-cb-pc-quiz-start");
  const startLabel = part("data-cb-pc-quiz-start-label");
  const replay = button("data-cb-pc-quiz-replay");
  const next = button("data-cb-pc-quiz-next");
  const progress = part("data-cb-pc-quiz-progress");
  const feedback = part("data-cb-pc-quiz-feedback");
  const answerGroup = part("data-cb-pc-quiz-answers");
  const answers = [
    ...section.querySelectorAll<HTMLButtonElement>("[data-cb-pc-quiz-answer]"),
  ];

  const enter = (state: QuizState): void => {
    section.dataset["state"] = state;
    show(start, state === "idle" || state === "done");
    show(replay, state === "asking" || state === "answered");
    show(answerGroup, state === "asking" || state === "answered");
    show(next, state === "answered");
    show(progress, state !== "idle");

    for (const answer of answers) {
      answer.disabled = state !== "asking";

      if (state === "asking") {
        answer.classList.remove("is-right", "is-wrong");
      }
    }

    if (progress !== null && state !== "idle") {
      progress.textContent =
        state === "done"
          ? `Round complete. You got ${String(quiz.score.right)} out of ${String(quiz.length)} correct.`
          : `Syllable ${String(quiz.number)} of ${String(quiz.length)}. ${String(quiz.score.right)} correct so far.`;
    }

    if (startLabel !== null) {
      startLabel.textContent =
        state === "done" ? "Start a new round" : "Start round";
    }

    if (next !== null) {
      next.textContent =
        quiz.score.asked >= quiz.length ? "See your score" : "Next syllable";
    }
  };

  const hear = (): void => {
    const question = quiz.current;

    if (question?.tone.audio !== undefined) {
      void player.play([
        { src: voiceFile(question.tone.audio, question.voice) },
      ]);
    }
  };

  /** Put a question to the reader, or say there was none to put. */
  const present = (question: unknown): void => {
    if (question === undefined) {
      enter("done");
      start?.focus();

      return;
    }

    if (feedback !== null) {
      feedback.textContent = "Which tone did you hear?";
    }

    enter("asking");
    answers[0]?.focus();
    hear();
  };

  start?.addEventListener("click", () => {
    present(quiz.start());
  });

  replay?.addEventListener("click", hear);

  next?.addEventListener("click", () => {
    if (quiz.score.asked >= quiz.length) {
      if (feedback !== null) {
        feedback.textContent = "";
      }

      enter("done");
      start?.focus();
    } else {
      present(quiz.next());
    }
  });

  for (const answer of answers) {
    answer.addEventListener("click", () => {
      const question = quiz.current;

      if (question === undefined || quiz.answered) {
        return;
      }

      const chosen = Number(answer.dataset["cbPcQuizAnswer"]);
      const right = quiz.answer(chosen) === true;
      const example = question.tone.examples[0];
      const said = `${question.tone.pinyin}${example === undefined ? "" : `, as in ${example.hanzi}`}`;

      for (const other of answers) {
        const tone = Number(other.dataset["cbPcQuizAnswer"]);

        other.classList.toggle("is-right", tone === question.tone.tone);
        other.classList.toggle("is-wrong", tone === chosen && !right);
      }

      // A chart tone is 1 to 4, each of which has its ordinal.
      const ordinal = toneOrdinals[question.tone.tone]!;

      if (feedback !== null) {
        feedback.textContent = right
          ? `Correct. You heard ${said}. That was the ${ordinal} tone.`
          : `Incorrect. You heard ${said}. That was the ${ordinal} tone.`;
      }

      enter("answered");
      next?.focus();
    });
  }

  enter("idle");
}
